import * as React from "react";
import { Link } from "react-router-dom";

import { useTheme } from "../../../providers/ThemeContext";
import { GlobalStyles } from "../../../styles/GlobalStyles";
import { CardStyles } from "./CardStyles";

type Props = {
  title: string;
  desc?: string;
  img: string;
  alt?: string;
  to?: string;
  onMore?: () => void;
  interactive?: boolean;
  className?: string;
  style?: React.CSSProperties;
  titleSize?: number | string;
  titleColor?: string;
  titleWeight?: number;
  titleAlign?: "start" | "center" | "end" | "left" | "right";
  radius?: number;
  imgRadius?: number | string;
  squareImage?: boolean;
  titleFontFamily?: React.CSSProperties["fontFamily"];
  cardColor?: string;
  descColor?: string;
  descOpacity?: number;
  variant?: "default" | "serviceLarge";
  headingTag?: "div" | "h2" | "h3";
};

const Card = ({
  title,
  desc,
  img,
  alt,
  to,
  onMore,
  interactive,
  className,
  style,
  titleSize,
  titleColor,
  titleWeight,
  titleAlign = "left",
  radius,
  imgRadius,
  squareImage,
  titleFontFamily,
  cardColor,
  descColor,
  descOpacity,
  variant = "default",
  headingTag = "div",
}: Props) => {
  const { theme } = useTheme();
  const s = CardStyles(theme);
  const g = GlobalStyles(theme);

  const isClickable = Boolean((to || onMore) && interactive !== false);

  const [hovered, setHovered] = React.useState(false);

  const Wrapper: React.ElementType = to && isClickable ? Link : "article";
  const wrapperProps = to && isClickable ? { to } : {};

  const defaultImgRadius = 12;
  const effectiveImgRadius = squareImage ? 0 : imgRadius ?? defaultImgRadius;

  return (
    <Wrapper
      {...(wrapperProps as any)}
      className={className}
      style={{
        ...g.card,
        ...(isClickable && hovered ? s.cardHover : {}),
        cursor: isClickable ? "pointer" : "default",
        ...(style || {}),
        borderRadius: radius ?? 16,
        borderTopLeftRadius: radius ?? 16,
        borderTopRightRadius: radius ?? 16,
        overflow: "hidden",
        background: cardColor ? cardColor : theme.card2,
      }}
      onMouseEnter={isClickable ? () => setHovered(true) : undefined}
      onMouseLeave={isClickable ? () => setHovered(false) : undefined}
      onFocus={isClickable ? () => setHovered(true) : undefined}
      onBlur={isClickable ? () => setHovered(false) : undefined}
      role={!to && isClickable ? "button" : undefined}
      tabIndex={!to && isClickable ? 0 : undefined}
      onKeyDown={
        !to && isClickable
          ? (e: React.KeyboardEvent) => {
              if ((e.key === "Enter" || e.key === " ") && onMore) onMore();
            }
          : undefined
      }
      aria-disabled={isClickable ? undefined : true}
    >
      {/* Preview image */}
      <div
        style={{
          ...s.previewWrap,
        }}
      >
        <img
          src={img}
          alt={alt ?? title}
          loading="lazy"
          style={{
            ...s.preview,
            ...(isClickable && hovered ? s.previewHover : {}),
            objectFit: "contain",
          }}
        />
        <span
          style={{ ...s.previewMask, borderRadius: effectiveImgRadius as any }}
        />
      </div>

      {/* Body */}
      <div style={s.body}>
        {React.createElement(headingTag, { style: s.cardTitle }, title)}
        {desc ? (
          <div
            style={{
              ...s.blurb,
              whiteSpace: "pre-line",
              color: descColor ?? s.blurb.color,
              opacity: descOpacity ?? s.blurb.opacity,
            }}
          >
            {desc}
          </div>
        ) : null}

        {onMore && interactive !== false && (
          <button
            type="button"
            style={s.moreBtn}
            onClick={(e) => {
              if (to) e.preventDefault();
              onMore();
            }}
          >
            More
          </button>
        )}
      </div>
    </Wrapper>
  );
};

export default Card;
